// Shared metric definitions (TimeKeep Page System spec, "Metrics" glossary).
//
// Before this module, Org Overview, Client Reports and Team Analytics each had
// their own idea of revenue and pay, so the same month showed three margins:
//   - Overview netted invoices against APPROVED statements only (pay = $0 until
//     anything was approved -> 100% margin);
//   - Client Reports used approved statement lines, else an estimate;
//   - Team Analytics ALWAYS estimated, never looking at approved statements,
//     and priced every member's hours at the full client rate (a retainer was
//     counted once per member).
// Every page now calls these two functions, so the numbers can't drift apart.
//
//   workValue  = billable hours, rounded per client per month, priced by the
//                billing terms in effect that month; retainers count in full for
//                every month the client is active.
//   memberPay  = every approved member invoice's lines (any seat type, and
//                write-in payments to people with no account); for pay-tracked
//                members in months without one, an estimate from assignments
//                (computeEarnings). Expense reimbursements are never pay.

import { computeEarnings } from './earnings.js';
import { calcOwed, calcOwedFromTerms, BILLING_MODELS } from './billingModels.js';

export const COMP_SEAT_TYPES = ['contractor', 'employee_hourly', 'employee_salary'];
export const hasCompSeat = (m) => COMP_SEAT_TYPES.includes(m?.seat_type);

// Client billing rounding (CLAUDE.md "Billing rounding"): 1-20 min -> 15,
// then 15-minute steps. Applied to a client's MONTHLY total, never per entry,
// and never to member pay.
export function roundBillingHours(hrs) {
  const mins = (Number(hrs) || 0) * 60;
  if (mins <= 0) return 0;
  if (mins <= 20) return 0.25;
  return Math.ceil((mins - 5) / 15) * 15 / 60;
}

/**
 * Spread a rounded monthly total back across the lines it came from.
 *
 * Rounding is a property of the MONTH, not of a task or an entry (see
 * roundBillingHours). But an invoice shows lines, and a client reading it
 * expects the lines to add up to what they are being charged. So the total is
 * rounded once, and the difference — at most a few minutes — is given to the
 * largest line, where it is least visible and least likely to make a line read
 * as nonsense.
 *
 * Returns the adjusted hours in the same order as `raws`. The sum is exactly
 * `roundedTotal` (to the cent-equivalent precision of hours).
 *
 * Rounding per line instead is what TimeKeep actually did until 2026-10-12, and
 * it quietly billed more: three entries of 6, 33 and 11 minutes round to 15, 30
 * and 15 = one hour, where the month's 50 minutes rounds to 45.
 */
export function distributeRoundedHours(raws, roundedTotal) {
  const list = (raws || []).map((h) => Number(h) || 0);
  if (!list.length) return [];
  const rawTotal = list.reduce((a, b) => a + b, 0);
  const target = Number(roundedTotal) || 0;
  if (rawTotal === 0) {
    // Nothing worked, so there is nothing to spread; put it all on the first
    // line rather than inventing a split.
    return list.map((_, i) => (i === 0 ? target : 0));
  }
  const diff = target - rawTotal;
  if (Math.abs(diff) < 1e-9) return list;
  let big = 0;
  for (let i = 1; i < list.length; i++) if (list[i] > list[big]) big = i;
  const out = list.slice();
  out[big] = Math.max(0, Math.round((out[big] + diff) * 1e6) / 1e6);
  return out;
}

// ── Months ────────────────────────────────────────────────────────────────────
const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
export function monthsBetween(startISO, endISO) {
  if (!startISO || !endISO) return [];
  const out = [];
  const d = new Date(startISO.slice(0, 7) + '-01T12:00:00');
  const end = endISO.slice(0, 7);
  while (ym(d) <= end) { out.push(ym(d)); d.setMonth(d.getMonth() + 1); }
  return out;
}
const monthStart = (m) => `${m}-01`;
const monthEndExcl = (m) => { const [y, mo] = m.split('-').map(Number); return ym(new Date(y, mo, 1, 12)) + '-01'; };

// Was a record (client, assignment, membership) live at any point in month m?
function liveInMonth(rec, m, { startKey = 'created_at', endKey = 'ended_at' } = {}) {
  const start = rec?.[startKey]?.slice(0, 10);
  const end = rec?.[endKey]?.slice(0, 10);
  if (start && start >= monthEndExcl(m)) return false;
  if (end && end < monthStart(m)) return false;
  return true;
}

// The terms row covering the first day of the month (half-open ranges).
export function termsForMonth(termsRows, clientId, m) {
  if (!termsRows) return null;
  const d = monthStart(m);
  return termsRows.find(t => t.client_id === clientId && t.effective_from <= d && (!t.effective_to || t.effective_to > d)) || null;
}

const isBillable = (e) => e.is_billable !== false;

// ── Work value ────────────────────────────────────────────────────────────────
// client:  clients row (cache columns used when no terms rows are supplied)
// entries: that client's entries (any member, any date -- filtered here)
// months:  ['YYYY-MM', ...] the range covers
// terms:   client_billing_terms rows for the org, or null to use the clients.* cache
// Returns { total, hours, roundedHours, byMonth: { m: { hours, rounded, amount } } }
export function workValue({ client, entries, months, terms = null }) {
  const out = { total: 0, hours: 0, roundedHours: 0, byMonth: {} };
  if (!client || client.is_internal) return out;
  for (const m of months) {
    const hrs = entries.reduce((s, e) => s + (isBillable(e) && e.entry_date?.slice(0, 7) === m ? parseFloat(e.hours || 0) : 0), 0);
    const rounded = roundBillingHours(hrs);
    let amount = 0;
    const row = termsForMonth(terms, client.id, m);
    if (row) {
      const fixed = BILLING_MODELS[row.billing_model_id]?.isFixedMonthly;
      amount = (fixed && !liveInMonth(client, m, { startKey: null })) ? 0 : calcOwedFromTerms(row, rounded);
    } else if (!terms || hrs > 0) {
      // No terms data (or none covering this month but work was logged): the cache.
      const fixed = BILLING_MODELS[client.model]?.isFixedMonthly;
      amount = (fixed && hrs === 0 && !liveInMonth(client, m)) ? 0 : calcOwed(client, rounded);
    }
    out.byMonth[m] = { hours: hrs, rounded, amount };
    out.total += amount; out.hours += hrs; out.roundedHours += rounded;
  }
  return out;
}

// ── Member pay ────────────────────────────────────────────────────────────────
const isReimbursement = (li) => li?.basis === 'reimbursement' || !!li?.expense_id;

// members:     org_members rows (any seat type; only comp-tracked seats count)
// assignments: assignments rows for the org
// entries:     time entries (any client/member; filtered here by month)
// statements:  contractor_statements rows for the org
// clients:     clients rows (computeEarnings needs the row for percent pay)
// months:      ['YYYY-MM', ...]
// Returns {
//   total, byClient: { clientId | '_none': amount }, byMember: { userId: amount },
//   byMemberClient: { 'userId|clientId': amount }, byMonth: { 'YYYY-MM': amount },
//   memberMonths: number of member-months with pay, approvedMemberMonths
// }
export function memberPay({ members, assignments, entries, statements, clients, months }) {
  const res = { total: 0, byClient: {}, byMember: {}, byMemberClient: {}, byMonth: {}, memberMonths: 0, approvedMemberMonths: 0 };
  const add = (uid, cid, amt) => {
    if (!amt) return;
    const c = cid || '_none';
    res.total += amt;
    res.byClient[c] = (res.byClient[c] || 0) + amt;
    res.byMember[uid] = (res.byMember[uid] || 0) + amt;
    res.byMemberClient[`${uid}|${c}`] = (res.byMemberClient[`${uid}|${c}`] || 0) + amt;
  };
  const clientById = (id) => clients.find(c => c.id === id);
  const monthSet = new Set(months);

  // 1. Every approved member invoice in range is pay, whoever it's for: a
  //    member of any seat type, or a write-in payment to someone with no
  //    account (user_id null, from "Log a Payment"). It's what was actually paid.
  const approvedFor = new Set(); // 'userId|YYYY-MM' covered by an approved invoice
  for (const st of statements || []) {
    if (st.status !== 'approved' || !monthSet.has(st.period)) continue;
    const uid = st.user_id || `_writein:${st.id || st.payee_name || ''}`;
    let total = 0;
    for (const li of (Array.isArray(st.line_items) ? st.line_items : [])) {
      if (isReimbursement(li)) continue;
      const amt = parseFloat(li.line_total || 0);
      add(uid, li.client_id, amt); total += amt;
      res.byMonth[st.period] = (res.byMonth[st.period] || 0) + amt;
    }
    if (st.user_id) approvedFor.add(`${st.user_id}|${st.period}`);
    if (total > 0) { res.memberMonths += 1; res.approvedMemberMonths += 1; }
  }

  // 2. Members on a pay-tracked seat, for months without an approved invoice:
  //    an estimate from their assignments. One pass per person (a member can
  //    have several tenure rows).
  const byUser = new Map();
  for (const m of members || []) if (hasCompSeat(m)) { if (!byUser.has(m.user_id)) byUser.set(m.user_id, []); byUser.get(m.user_id).push(m); }

  for (const [uid, tenures] of byUser) {
    const myAsg = (assignments || []).filter(a => a.user_id === uid);
    const myEntries = (entries || []).filter(e => e.user_id === uid);
    for (const mo of months) {
      if (approvedFor.has(`${uid}|${mo}`)) continue;
      const moEntries = myEntries.filter(e => e.entry_date?.slice(0, 7) === mo);
      // Tenure dates only limit months with no logged work: logged time proves
      // the person worked that month even when started_at is later (history
      // imported in bulk, a re-hire, a backfill).
      const tenure = tenures.find(t => liveInMonth(t, mo, { startKey: 'started_at' }));
      if (!tenure && moEntries.length === 0) continue;
      const rateSource = tenure || tenures[tenures.length - 1];
      let monthTotal = 0;
      const assignedClients = new Set();
      for (const a of myAsg) {
        if (!liveInMonth(a, mo)) continue;
        assignedClients.add(a.client_id);
        const calc = computeEarnings(a, moEntries.filter(e => e.client_id === a.client_id), mo, clientById(a.client_id));
        if (calc.appliesThisPeriod && calc.lineTotal) { add(uid, a.client_id, calc.lineTotal); monthTotal += calc.lineTotal; }
      }
      // Work logged for a client with no assignment on record (e.g. one that
      // ended): flat hourly estimate at the member's own compensation rate.
      const rate = parseFloat(rateSource?.compensation_rate || 0);
      if (rate) {
        const loose = {};
        for (const e of moEntries) if (e.client_id && !assignedClients.has(e.client_id)) loose[e.client_id] = (loose[e.client_id] || 0) + parseFloat(e.hours || 0);
        for (const [cid, hrs] of Object.entries(loose)) { add(uid, cid, hrs * rate); monthTotal += hrs * rate; }
      }
      if (monthTotal > 0) {
        res.byMonth[mo] = (res.byMonth[mo] || 0) + monthTotal;
        res.memberMonths += 1;
      }
    }
  }
  return res;
}

// Tile text for the Provisional chip, or null when every figure is final.
export function provisionalNote(pay) {
  if (!pay || pay.memberMonths === 0 || pay.approvedMemberMonths === pay.memberMonths) return null;
  return `${pay.approvedMemberMonths} of ${pay.memberMonths} member invoice${pay.memberMonths === 1 ? '' : 's'} approved`;
}

export const marginPct = (revenue, cost) => (revenue > 0 ? ((revenue - cost) / revenue) * 100 : null);
